import { ProcessedSearchResults } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { useAddItemToCart } from '@app/hooks/useAddItemToCart';
import { getPreviewUrl } from '@app/utils/common';
import Box from '@redbubble/design-system/src/Box';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { useViewportWidth } from '@redbubble/next/hooks';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import {
  AnalyticsATCContext,
  AnalyticsItemsList,
  AnalyticsPageType,
} from '@redbubble/next-analytics/gtm';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { SearchResultsPageProps } from 'pages/shop/types';
import React, { useCallback, useEffect, useRef } from 'react';
import { RecommendedProducts } from '../../../../types/shop-bff/types';
import { trackGA4AddToCartEvent } from '../../../../utils/analytics';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import { DEFAULT_PER_PAGE_RESULTS } from '../../constants';
import { trackGA4SRPViewItemListEvent } from '../../utils/analytics';
import { InViewCallback, SearchResultCard } from '../SearchResultCard';
import { SimilarProductsSection } from '../SimilarProductsSection';
import { SubscribeBanner } from '../SubscribeBanner';
import styles from './SearchResultsGrid.module.scss';
import { SearchResultsGridSkeleton } from './SearchResultsGridSkeleton';

const MOBILE_GA_ROW_SIZE = 2;
const DESKTOP_GA_ROW_SIZE = 6;
const ANALYTICS_ROWS = 3;

const PREVIEW_TYPE_ID = 'artwork';

const MIN_RESULTS_FOR_SUBSCRIBE_BANNER_MOBILE = 10;
const MIN_RESULTS_FOR_SUBSCRIBE_BANNER_TABLET = 15;
const MIN_RESULTS_FOR_SUBSCRIBE_BANNER_DESKTOP = 30;

const getMinResultsForSubscribeBanner = (deviceType: DeviceType) => {
  if (deviceType === DeviceType.Mobile) {
    return MIN_RESULTS_FOR_SUBSCRIBE_BANNER_MOBILE;
  }

  if (deviceType === DeviceType.Tablet) {
    return MIN_RESULTS_FOR_SUBSCRIBE_BANNER_TABLET;
  }

  return MIN_RESULTS_FOR_SUBSCRIBE_BANNER_DESKTOP;
};

interface SearchResultsGridProps {
  searchResults: ProcessedSearchResults;
  recommendedProducts: RecommendedProducts;
  lastClickedItemId: SearchResultsPageProps['lastClickedItemId'];
  loading: boolean;
  limit?: number;
  searchUUID: SearchResultsPageProps['searchUUID'];
  topic: SearchResultsPageProps['topic'];
  categoryName?: string | null;
  showStandardPricing?: boolean;
}

export const SearchResultsGrid: React.FC<SearchResultsGridProps> = ({
  searchResults,
  recommendedProducts,
  lastClickedItemId,
  limit,
  loading,
  searchUUID,
  topic,
  showStandardPricing,
}) => {
  const { deviceType, pagination } = usePageProps<
    AppProps & SearchResultsPageProps
  >();

  const [{ isLoggedIn }] = useUserInfo<UserInfo>();
  const searchResultsGridRef = useRef<HTMLDivElement>(null);
  const searchResultsGridItemRef = useRef<HTMLDivElement>(null);

  const analyticsPaginationIndexOffset = pagination
    ? (pagination.perPage ?? DEFAULT_PER_PAGE_RESULTS) *
      (pagination.currentPage ? pagination.currentPage - 1 : 0)
    : 0;

  const onAddToCartSuccess = useCallback(
    (itemId: string, index: number) => {
      const addedItem = searchResults.find(
        (result) => result.inventoryItem?.id === itemId
      );

      if (
        addedItem &&
        addedItem.inventoryItem &&
        addedItem.inventoryItem.work
      ) {
        trackGA4AddToCartEvent({
          item: addedItem.inventoryItem,
          workId: addedItem.inventoryItem.work.id,
          pageType: AnalyticsPageType.SearchResultsPage,
          context: AnalyticsATCContext.SearchResultsPage,
          analyticsComponentID: AnalyticsItemsList.SearchResultsPageResults,
          index: index + analyticsPaginationIndexOffset,
          searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
          showStandardPricing,
        });
      }
    },
    [analyticsPaginationIndexOffset, searchResults, showStandardPricing]
  );

  const addItemToCart = useAddItemToCart(onAddToCartSuccess);

  const recommendedProductsPresent =
    Array.isArray(recommendedProducts) && recommendedProducts.length > 0;

  const { isOrAbove } = useViewportWidth();
  const isDesktop = isOrAbove(DeviceType.Desktop);

  const analyticsChunkSize = isDesktop
    ? DESKTOP_GA_ROW_SIZE * ANALYTICS_ROWS
    : MOBILE_GA_ROW_SIZE * ANALYTICS_ROWS;

  const lastRecordedAnalyticsIndex = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    lastRecordedAnalyticsIndex.current = 0;
  }, [searchResults]);

  if (loading) {
    return <SearchResultsGridSkeleton limit={limit} />;
  }

  const filteredSearchResults = limit
    ? searchResults.slice(0, limit)
    : searchResults;

  const showSubscribeBannerInGrid =
    !isLoggedIn &&
    filteredSearchResults.length > getMinResultsForSubscribeBanner(deviceType);

  return (
    <Box
      className={styles.grid}
      data-testid="search-results-grid"
      ref={searchResultsGridRef}
    >
      {filteredSearchResults.map((result, index) => {
        const { inventoryItem } = result;
        if (!inventoryItem) return null;

        const shouldTriggerInView = index % analyticsChunkSize === 0;

        const { id, productPageUrl } = inventoryItem;

        // TODO[DEP-3756]: pass correct preview type id based on screen size and selected preview
        const previewTypeId = result.defaultPreviewTypeId || PREVIEW_TYPE_ID;
        const imageSrc = getPreviewUrl(inventoryItem, previewTypeId);

        if (!imageSrc || !productPageUrl) return null;

        const analyticsItemIndex = index + analyticsPaginationIndexOffset;

        const onInView: InViewCallback = shouldTriggerInView
          ? (inView) => {
              if (inView) {
                // the following is required so we dont re-fire events on window resize
                // item index passed through here
                const endIndex = index + analyticsChunkSize;

                // determine start index of event items
                const startIndex =
                  // if the index is not the same as the previous endIndex
                  // this can happen when the window is resized or on refresh of the page
                  lastRecordedAnalyticsIndex.current !== index &&
                  // if the number of required items is greater than the analytics chunk size start from the current index
                  // this condition happens on refresh or whenever scroll position is retained and the results change
                  // adding this conditions prevents excessive refiring of events
                  !(
                    index - lastRecordedAnalyticsIndex.current >
                    analyticsChunkSize
                  )
                    ? lastRecordedAnalyticsIndex.current
                    : index;

                const slicedResults = filteredSearchResults.slice(
                  startIndex,
                  endIndex
                );

                if (slicedResults.length > 0) {
                  trackGA4SRPViewItemListEvent(
                    slicedResults,
                    AnalyticsItemsList.SearchResultsPageResults,
                    topic,
                    searchUUID,
                    analyticsItemIndex,
                    showStandardPricing
                  );
                  // set last recorded index
                  lastRecordedAnalyticsIndex.current = endIndex;
                }
              }
            }
          : null;

        return (
          <React.Fragment key={id}>
            <div ref={searchResultsGridItemRef}>
              <SearchResultCard
                inventoryItem={inventoryItem}
                groupId={result.groupId}
                artistIsEstablished={result.artistIsEstablished}
                imageSrc={imageSrc}
                onInView={onInView}
                addItemToCart={addItemToCart}
                index={index}
                analyticsItemIndex={analyticsItemIndex}
                rsLocation={RSProductClickedLocation.SearchResultsPageResults}
                rsPageType={RSPageType.Search}
                showStandardPricing={showStandardPricing}
              />
            </div>
            {recommendedProductsPresent && id === lastClickedItemId && (
              <ErrorBoundary>
                <SimilarProductsSection
                  lastClickedItem={result}
                  recommendedProducts={recommendedProducts}
                />
              </ErrorBoundary>
            )}
          </React.Fragment>
        );
      })}

      {showSubscribeBannerInGrid && (
        <ErrorBoundary>
          <SubscribeBanner />
        </ErrorBoundary>
      )}
    </Box>
  );
};
